import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'hooks/store';
import {
  cleanOrderItemsAction,
  getOrderItemListAction,
  isLoadingOrderItemsSelector,
  orderItemsEntitiesSelector,
  orderItemsIdsSelector,
  remoteTotalSelector,
} from 'state/slices/orderItem';
import { usePagination } from 'hooks/pagination';
import {
  Order,
  OrderItem,
  Organization,
  PaginationData,
  ProductionItem,
} from 'constants/interface';
import { Search, Table, Tag } from 'UI';
import {
  OrderItemApiStatusDisplayMapping,
  OrderItemApiStatusEnum,
  OrderTypeDisplayMapping,
  OrderTypeEnum,
  OrderUnitDisplayMapping,
  OrderUnitEnum,
} from 'constants/order';
import { buildOrderItemProductDisplay, buildSimpleQuantityDisplay, getOrderItemApiStatus } from 'helpers/order';
import { formatDate, parseUtcStringDate } from 'helpers/utils';

import TableRowAction from './components/TableRowAction';

import {
  SearchContainer,
} from './style';

interface OrderItemsTabProps {
  selectedOrg: Organization
}

const OrderItemsTab = ({
  selectedOrg,
}: OrderItemsTabProps): JSX.Element => {
  const dispatch = useDispatch();
  const remoteTotal = useSelector(remoteTotalSelector);

  const orderItemsEntities = useSelector(orderItemsEntitiesSelector);
  const orderItemsIds = useSelector(orderItemsIdsSelector);
  const isRemoteLoading = useSelector(isLoadingOrderItemsSelector);

  const orderItems = orderItemsIds.map((orderItemId) => {
    const entity = orderItemsEntities[orderItemId];
    if (!entity) return undefined;
    const tableOrderItem: OrderItem & {
      key?: string,
    } = {
      ...entity,
      key: entity.id,
    };
    return tableOrderItem;
  }).filter((tableOrderItem) => !!tableOrderItem) as (OrderItem & { key: string})[];

  // const [currentActiveAction, setCurrentActiveAction] = useState<number>();

  const remoteFetch = (
    paginationData: PaginationData,
  ) => {
    // TODO: use generics for filter type check
    const status = paginationData.filter?.status as OrderItemApiStatusEnum
      || OrderItemApiStatusEnum.NON_CANCELED;
    dispatch(getOrderItemListAction({
      ...paginationData,
      status,
    }));
  };
  const pagination = usePagination<OrderItem>(
    remoteFetch,
    {
      filter: {
        status: undefined,
        organization: selectedOrg.id,
      },
      order: 'descend',
    },
  );

  useEffect(() => {
    dispatch(cleanOrderItemsAction());
  }, []);

  useEffect(() => {
    pagination.updateTotal(remoteTotal);
  }, [remoteTotal]);

  // const sorter = (): number => 0;

  const pageData = pagination.getInternal();

  const orderTypeFilter = pageData.filter.type ?? null;
  const orderStatusFilter = pageData.filter.status ?? null;

  const statusFilterList = [
    OrderItemApiStatusEnum.SCHEDULED,
    OrderItemApiStatusEnum.PENDING_ADVANCE,
    OrderItemApiStatusEnum.CONFIRMED,
    OrderItemApiStatusEnum.SEEDING,
    OrderItemApiStatusEnum.PRODUCTION,
    OrderItemApiStatusEnum.DELIVERY,
    OrderItemApiStatusEnum.FINISHED,
    OrderItemApiStatusEnum.CANCELED,
  ];

  return (
    <>
      <SearchContainer>
        <Search placeholder="Buscar itens" onSearch={pagination.onSearch} allowClear />
      </SearchContainer>
      <Table<OrderItem>
        dataSource={orderItems}
        pagination={pageData}
        loading={isRemoteLoading}
        showSorterTooltip={false}
        locale={{
          filterReset: 'Limpar',
          cancelSort: 'Clique para limpar ordenação',
          triggerAsc: 'Clique para ordem crescente',
          triggerDesc: 'Clique para ordem decrescente',
        }}
        onChange={pagination.onTableChange}
      >
        <Table.Column
          title="OS​"
          key="seq"
          // dataIndex="seq"
          // sorter={sorter}
          // sortOrder={
          //   pageData.orderBy === 'seq'
          //     ? pageData.order
          //     : undefined
          // }
          dataIndex="order"
          render={(
            order: Order,
          ) => order.seq}
        />
        <Table.Column
          title="Cliente"
          key="client"
          dataIndex="order"
          render={(
            order: Order,
          ) => order.client.name}
        />
        <Table.Column
          title="Localização"
          key="location"
          dataIndex="order"
          render={(
            order: Order,
          ) => {
            const { location, client } = order;
            const display = location
              ? `${location.name} - ${location.city}/${location.state} (${location.maintainer})`
              : `${client.city}/${client.state}`;
            return display;
          }}
        />
        <Table.Column
          title="Formação​"
          key="type"
          dataIndex="order"
          filters={Object.values(OrderTypeEnum).map((orderType) => ({
            text: OrderTypeDisplayMapping[orderType].name,
            value: orderType,
          }))}
          filterMultiple={false}
          filteredValue={orderTypeFilter !== null ? [orderTypeFilter] : null}
          render={(order: Order) => {
            const displayName = OrderTypeDisplayMapping[order.type].name;
            return displayName;
          }}
        />
        <Table.Column
          title="Data do semeio prev"
          key="plannedSeedDate"
          dataIndex="plannedSeedDate"
          // filters={Object.values(OrderTypeEnum).map((orderType) => ({
          //   text: OrderTypeDisplayMapping[orderType].name,
          //   value: orderType,
          // }))}
          // filterMultiple={false}
          // filteredValue={orderTypeFilter !== null ? [orderTypeFilter] : null}
          render={(plannedSeedDate: string) => {
            const display = formatDate(parseUtcStringDate(plannedSeedDate));
            return display;
          }}
        />
        <Table.Column
          title="Produto"
          key="product"
          dataIndex="productionItems"
          render={(
            productionItems: ProductionItem[],
            orderItem: OrderItem,
          ) => {
            const display = buildOrderItemProductDisplay(orderItem);
            return display;
          }}
        />
        <Table.Column
          title="Unid"
          key="product"
          dataIndex="unit"
          render={(
            unit: OrderUnitEnum,
          ) => {
            const display = OrderUnitDisplayMapping[unit].smallName;
            return display;
          }}
        />
        <Table.Column
          title="Qtd"
          key="product"
          dataIndex="orderQuantity"
          render={(
            orderQuantity: number,
          ) => {
            const display = buildSimpleQuantityDisplay(orderQuantity);
            return display;
          }}
        />
        <Table.Column
          title="Status"
          key="status"
          dataIndex="order"
          filters={statusFilterList.map((statusType) => ({
            text: OrderItemApiStatusDisplayMapping[statusType].name,
            value: statusType,
          }))}
          filterMultiple={false}
          filteredValue={orderStatusFilter !== null ? [orderStatusFilter] : null}
          render={(order: Order, orderItem: OrderItem) => {
            const status = getOrderItemApiStatus(orderItem);
            const displayName = OrderItemApiStatusDisplayMapping[status].name;
            return (
              <Tag color={OrderItemApiStatusDisplayMapping[status].color}>
                {displayName}
              </Tag>
            );
          }}
        />
        <Table.Column
          title=""
          key="action"
          dataIndex="id"
          render={(id: string, record: OrderItem) => {
            const actionButton = (
              <TableRowAction
                orderItem={record}
                // index={index}
                // onVisibleChange={(visibleIndex) => setCurrentActiveAction(visibleIndex)}
              />
            );
            return actionButton;
          }}
        />
      </Table>
    </>
  );
};

export default OrderItemsTab;
